import React from 'react';
import {FlexRow, Link, Pill, PillVariant, Text} from '@forecast-it/design-system';
import {Column} from '../Elements.styled';
import {
	arePhaseTasksComplete,
	derivePhaseSignoffStatus,
	PHASE_SIGNOFF_STATUS_LABEL_ID,
	PhaseSignoffStatus,
	resolveSignoffApproverLabel,
} from '../../../../../util/phaseSignoffStatus';
import {showSendSignoffModal} from '../../../../modals/send-signoff/SendSignoffModal';
import {confirmCancelPhaseSignoffRequest} from '../../../../modals/send-signoff/cancelPhaseSignoffRequest';

// Visual weight for each state: SUCCESS for the terminal signed-off row,
// INFORMATION for states where the firm or its client has an action to take,
// NEUTRAL for the passive states. Pill supplies its own accessible contrast in
// both light and dark themes so the label stays legible.
const SIGNOFF_STATUS_PILL_VARIANT = {
	[PhaseSignoffStatus.NOT_CONFIGURED]: PillVariant.NEUTRAL,
	[PhaseSignoffStatus.REQUIRED_BUT_INCOMPLETE]: PillVariant.NEUTRAL,
	[PhaseSignoffStatus.READY_TO_SEND]: PillVariant.INFORMATION,
	[PhaseSignoffStatus.AWAITING_CLIENT]: PillVariant.INFORMATION,
	[PhaseSignoffStatus.CHANGES_REQUESTED]: PillVariant.INFORMATION,
	[PhaseSignoffStatus.SIGNED_OFF]: PillVariant.SUCCESS,
};

/**
 * The sign-off lock moment in the viewer's locale. Returns null for a missing or
 * unparseable timestamp, so the badge degrades to the plain state label rather
 * than printing "Invalid date".
 */
const formatLockedDate = (isoString, intl) => {
	if (!isoString) return null;
	const parsed = new Date(isoString);
	if (Number.isNaN(parsed.getTime())) return null;
	return intl.formatDate(parsed, {year: 'numeric', month: 'short', day: 'numeric'});
};

/**
 * A `YYYY-MM-DD` calendar date — the shape the sign-off deadline is stored and sent in.
 * Pinned to UTC on purpose: the string names a day, not an instant, so rendering it in
 * the viewer's zone would print the day before it for anyone west of UTC. Null for a
 * missing or unparseable value, so the cell shows the bare state instead.
 */
const formatCalendarDate = (dateString, intl) => {
	if (!dateString) return null;
	const parsed = new Date(`${dateString}T00:00:00Z`);
	if (Number.isNaN(parsed.getTime())) return null;
	return intl.formatDate(parsed, {year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC'});
};

/**
 * The Sign-off Status cell of a phase header row: a state pill for one of the six
 * client sign-off states, plus whichever detail that state has to offer — when a locked
 * phase was signed off and by whom, the deadline an outstanding request is due by, or
 * the note a client attached when they asked for changes.
 *
 * Two of the six states are also action surfaces, and only those two. *Ready to send*
 * makes the pill itself open the Send sign-off modal; *Awaiting client* offers a cancel
 * that withdraws the request. The other four are labels.
 *
 * Every detail degrades independently, and none is asserted when it cannot be read: an
 * approver who has lost workspace eligibility drops out of `portalEligibleApprovers`, a
 * request may carry no deadline, and a client may have asked for changes without saying
 * what. The cell composes whatever it actually has and falls back to the bare pill.
 *
 * `phase` may be null (a header row with no phase), in which case the cell reports
 * Not configured rather than rendering blank. A phase with no resolvable `project`
 * renders the Ready-to-send label without the action: the modal needs the parent to
 * anchor the request to a client org, so offering a send that must fail is worse than
 * offering none.
 */
export const PhaseSignoffStatusColumn = ({phase, project, rolledUpValues, portalEligibleApprovers, width, align, intl}) => {
	const portalConfig = phase && phase.portalConfig ? phase.portalConfig : null;
	const status = derivePhaseSignoffStatus({
		requiresClientSignoff: portalConfig ? portalConfig.requiresClientSignoff : false,
		signoffLocked: portalConfig ? portalConfig.signoffLocked : false,
		tasksComplete: arePhaseTasksComplete(rolledUpValues),
		requestState: portalConfig ? portalConfig.signoffRequestState : null,
	});
	const label = intl.formatMessage({id: PHASE_SIGNOFF_STATUS_LABEL_ID[status]});

	const isSignedOff = status === PhaseSignoffStatus.SIGNED_OFF;
	const lockedDate = isSignedOff && portalConfig ? formatLockedDate(portalConfig.signoffLockedAt, intl) : null;
	const approverName = isSignedOff
		? resolveSignoffApproverLabel(portalConfig ? portalConfig.signoffApproverPortalUserId : null, portalEligibleApprovers)
		: null;

	// One detail slot, shared by the three states that have something to add beside the
	// pill — which is also what keeps the cell's width behaviour identical across them.
	let detail = null;
	if (lockedDate && approverName) {
		detail = intl.formatMessage(
			{id: 'client_portal.signoff_state.signed_off_detail_date_name'},
			{date: lockedDate, name: approverName}
		);
	} else if (lockedDate) {
		detail = lockedDate;
	} else if (approverName) {
		detail = intl.formatMessage({id: 'client_portal.signoff_state.signed_off_detail_name'}, {name: approverName});
	} else if (status === PhaseSignoffStatus.AWAITING_CLIENT) {
		const deadline = formatCalendarDate(portalConfig ? portalConfig.signoffRequestDeadline : null, intl);
		detail = deadline
			? intl.formatMessage({id: 'client_portal.signoff_state.awaiting_client_deadline'}, {date: deadline})
			: null;
	} else if (status === PhaseSignoffStatus.CHANGES_REQUESTED) {
		detail = (portalConfig && portalConfig.signoffChangesRequestedNote) || null;
	}

	const pill = <Pill variant={SIGNOFF_STATUS_PILL_VARIANT[status]}>{label}</Pill>;
	const phaseName = (phase && phase.name) || '';
	const canSend = status === PhaseSignoffStatus.READY_TO_SEND && phase && phase.id && project && project.id;

	/**
	 * The phase header row collapses and expands on a click anywhere in it, so an action
	 * inside one of its cells has to stop the event or it does its own job AND folds the
	 * phase away underneath the dialog it just opened. Same guard the editable phase-name
	 * and custom-field cells in this row already use.
	 */
	const action = handler => event => {
		event.stopPropagation();
		event.preventDefault();
		handler();
	};

	return (
		<Column width={width} align={align}>
			<FlexRow data-cy="phase-signoff-status">
				{canSend ? (
					<Link
						onClick={action(() =>
							showSendSignoffModal({
								phaseId: phase.id,
								projectId: project.id,
								phaseName,
								taskCount: rolledUpValues ? rolledUpValues.taskCount : null,
								doneCount: rolledUpValues ? rolledUpValues.doneCount : null,
								approverPortalUserId: portalConfig ? portalConfig.signoffApproverPortalUserId : null,
							})
						)}
						// The pill reads "Ready to send", which does not say what pressing it does,
						// and every such link on the page would otherwise share that one name.
						aria-label={intl.formatMessage(
							{id: 'client_portal.signoff_state.ready_to_send_action_aria'},
							{name: phaseName}
						)}
						data-cy="phase-signoff-send"
					>
						{pill}
					</Link>
				) : (
					pill
				)}
				{detail && (
					<Text color={'auxiliary'} ellipsis tooltipOnEllipsis data-cy="phase-signoff-detail">
						{detail}
					</Text>
				)}
				{status === PhaseSignoffStatus.AWAITING_CLIENT && phase && phase.id && (
					<Link
						onClick={action(() => confirmCancelPhaseSignoffRequest({phaseId: phase.id, phaseName, intl}))}
						aria-label={intl.formatMessage(
							{id: 'client_portal.signoff_state.cancel_request_aria'},
							{name: phaseName}
						)}
						data-cy="phase-signoff-cancel"
					>
						{intl.formatMessage({id: 'client_portal.signoff_state.cancel_request'})}
					</Link>
				)}
			</FlexRow>
		</Column>
	);
};
